import * as JsSIP from 'jssip';
import {
    SIP_PROXIES,
    SIP_PROXIES_FROM_PROVISIONING,
} from 'flow/actions/phone-actions';
import { b64EncodeUnicode } from 'flow/utils/meeting-utils';
import { MeetingLogLevel, addMeetingLog } from 'flow/utils/meeting-utils';
import {
    setActiveMeetingCallIdAction,
    setActiveMeetingCallSessionAction,
    setActiveMeetingCallUserAgentAction,
    setMicInUseAction,
} from 'flow/actions/meeting-actions';
import {
    selectActiveMeetingAudioReference,
    selectActiveMeetingCallSession,
    selectActiveMeetingCallUserAgent,
    selectActiveMeetingCode,
    selectActiveMeetingNumber,
    selectLobbyNumber,
    selectIsUserLoggedIn,
    selectSenderName,
    selectShouldUseExistingUserAgent,
    selectLobbyMicDeviceInUse,
} from 'flow/selectors/meeting-selectors';
import { getOwnCountryCodeOrNull } from 'flow/selectors/selectors';
import { getCountryCodeFromNumber } from 'flow/utils/call-utils';
import { parseUrlParameter } from 'flow/utils/browser-utils';
import {
    resolveMeetingSip,
    toSupportedMeetingRegion,
} from 'flow/utils/meeting-sip-region';

/**
 * Resolve the *conference's* region country code for meeting SIP selection, best conference-region
 * signal first:
 *   1. the invite-link `countryCode` param — the host's region, and the only reliable signal for
 *      guests (the external conference endpoint returns no number — see ConferenceDTOFactory).
 *   2. the conference number's country — works for in-app joins; null for guests.
 *   3. the joiner's own country — last resort; null for guests so it can't misroute them, and it
 *      matches the joiner's own SIP region for logged-in in-app joins (the same source the Joining
 *      Info modal uses). resolveMeetingSip applies the IS_UAE_HOST override and the Sweden default.
 */
export const resolveMeetingCountryCode = (state) => {
    const number = selectActiveMeetingNumber(state) || selectLobbyNumber(state);
    const parsedCountryCode = parseUrlParameter('countryCode');
    return (
        toSupportedMeetingRegion(parsedCountryCode) ||
        (number && getCountryCodeFromNumber(number)) ||
        getOwnCountryCodeOrNull(state)
    );
};

export const getUserAgent = () => async (dispatch, getState) => {
    const state = getState();
    /**
     * Select the right UserAgent to start the call with
     *
     * isUserLoggedIn = false       -> use the meeting userAgent
     * isUserLoggedIn = true
     *      & to use own SIP user   -> use phone.get('userAgent')
     *      & not own SIP user      -> use the meeting userAgent
     */
    let userAgent;
    const shouldUseExistingUserAgent = selectShouldUseExistingUserAgent(state);
    dispatch(
        addMeetingLog(
            `MeetingCallClient#getUserAgent should use an existing user agent: ${shouldUseExistingUserAgent}`,
        ),
    );

    if (shouldUseExistingUserAgent) {
        const phoneReducer = state.phone;
        userAgent = phoneReducer && phoneReducer.get('userAgent');
        if (!userAgent) {
            try {
                userAgent = await dispatch(createMeetingUserAgent());
            } catch (e) {
                dispatch(
                    addMeetingLog(
                        `MeetingCallClient#getUserAgent could not create a userAgent, ${e}`,
                    ),
                );
            }
        }
    } else {
        try {
            userAgent = await dispatch(createMeetingUserAgent());
        } catch (e) {
            dispatch(
                addMeetingLog(
                    `MeetingCallClient#getUserAgent could not create a userAgent, ${e}`,
                ),
            );
        }
    }
    return userAgent;
};

const createMeetingUserAgent = () => (dispatch, getState) => {
    return new Promise((resolve, reject) => {
        const state = getState();
        const countryCode = resolveMeetingCountryCode(state);
        const { uri, password, proxies } = resolveMeetingSip(countryCode);

        const finalProxies =
            selectIsUserLoggedIn(state) && SIP_PROXIES_FROM_PROVISIONING
                ? SIP_PROXIES
                : proxies;

        dispatch(
            addMeetingLog(
                'MeetingCallClient#createMeetingUserAgent create a new user agent',
            ),
        );
        const sockets = finalProxies.map((proxy) => {
            return {
                socket: new JsSIP.WebSocketInterface(proxy.socketUrl),
                weight: proxy.weight,
            };
        });

        const configuration = {
            connection_recovery_max_interval: 120,
            password,
            register: false,
            session_timers: false,
            sockets,
            uri,
            user_agent: 'public-meeting-client-tvx-web',
        };

        const timer = setTimeout(() => {
            return reject(
                'Creating a user agent took too long, took more than 5000ms',
            );
        }, 5000);
        const userAgent = new JsSIP.UA(configuration);
        userAgent.on('connected', () => {
            dispatch(setActiveMeetingCallUserAgentAction(userAgent));
            clearTimeout(timer);
            return resolve(userAgent);
        });
        userAgent.on('newRTCSession', (callData) => {
            const session = callData.session;
            const callId = callData.request.getHeader('Call-ID');
            session.data.callId = callId;
            session.data.isMeeting = true;
            dispatch(setActiveMeetingCallIdAction(callId));
        });
        userAgent.on('registrationFailed', (data) => {
            const response = (data && data.response) || 'No response';
            const cause = (data && data.cause) || 'No cause';
            return reject(
                `Failed to create useragent registration failed. Response: '${response}', cause: '${cause}'`,
            );
        });
        userAgent.on('disconnected', (data) => {
            const code = (data && data.code) || 'No code';
            const reason = (data && data.reason) || 'No reason';
            const error = (data && data.error) || 'Not an error';
            return reject(
                `Failed to create useragent disconnected. Code: '${code}', reason: '${reason}', error: '${error}'`,
            );
        });

        // TODO: #video Make a general stopper instead for call + video?
        // pagehide is not fully supported in Firefox
        window.addEventListener('beforeunload', () => userAgent.stop());

        userAgent.start();
    });
};

export const startCall = (userAgent) => (dispatch, getState) => {
    // eslint-disable-next-line consistent-return
    return new Promise((resolve, reject) => {
        const state = getState();
        const meetingCode = selectActiveMeetingCode(state);
        if (!meetingCode) {
            return reject('No meetingCode');
        }
        if (!userAgent) {
            return reject('No userAgent');
        }

        const senderName = selectSenderName(state);
        const micDevice = selectLobbyMicDeviceInUse(state);
        dispatch(setMicInUseAction(micDevice));
        const audioReference = selectActiveMeetingAudioReference(state);
        const mediaConstraints = {
            // noiseSuppression pinned on (not left to browser defaults) so the
            // sent track — which own-user speaking detection analyses — has a
            // consistent, denoised input across browsers.
            audio: {
                ...(micDevice?.deviceId
                    ? { deviceId: micDevice.deviceId }
                    : {}),
                noiseSuppression: true,
            },
            video: false,
        };
        dispatch(
            addMeetingLog(`MeetingCallClient#startCall will start a call, meetingCode: ${meetingCode}, 
        senderName: ${senderName}, micDeviceId: ${
            micDevice && micDevice.deviceId
        }`),
        );
        const eventHandlers = {
            progress: (data) => {
                if (data.originator === 'remote') {
                    data.response.body = null;
                }
            },
            failed: (data) => {
                dispatch(
                    addMeetingLog(
                        `MeetingCallClient#eventHandlers failed to start a call — originator: ${data?.originator}, cause: ${data?.cause}`,
                        MeetingLogLevel.ERROR,
                    ),
                );
                return reject('Start call failed');
            },
            confirmed: () => {
                dispatch(
                    addMeetingLog(
                        'MeetingCallClient#eventHandlers successfully started a call',
                    ),
                );
                const oldCallSession = selectActiveMeetingCallSession(state);
                if (oldCallSession) {
                    dispatch(
                        addMeetingLog(
                            'MeetingCallClient#eventHandlers found an existing call session when setting up new call, will end that session.',
                            MeetingLogLevel.WARN,
                        ),
                    );
                    try {
                        oldCallSession.terminate();
                    } catch (e) {
                        dispatch(
                            addMeetingLog(
                                `MeetingCallClient#eventHandlers Could not terminate call after setting up new call, ${
                                    e && e.message
                                }`,
                            ),
                        );
                    }
                }
                dispatch(setActiveMeetingCallSessionAction(session));
                return resolve(session);
            },
            ended: (data) => {
                dispatch(
                    addMeetingLog(
                        `MeetingCallClient#eventHandlers call ended — originator: ${data?.originator}, cause: ${data?.cause}`,
                        MeetingLogLevel.WARNING,
                    ),
                );
            },
            peerconnection: (data) => {
                const peerConnection = data && data.peerconnection;
                peerConnection.onaddstream = (e) => {
                    const meetingStream = e && e.stream;
                    if (audioReference) {
                        audioReference.srcObject = meetingStream;
                    }
                };
            },
        };

        const options = {
            eventHandlers,
            mediaConstraints,
            extraHeaders: [
                `X-TVX-CONFERENCE-MEETING-CODE: ${meetingCode}`,
                `X-TVX-CONFERENCE-DISPLAY-NAME: ${b64EncodeUnicode(
                    senderName,
                )}`,
                `X_TVX_CONFERENCE_MEMBER_UUID: ${senderName.slice(0, 36)}`,
                `X-TVX-IS-VIDEO-MEETING-CALL: ${true}`,
            ],
        };

        const { dialUri } = resolveMeetingSip(resolveMeetingCountryCode(state));

        const session = userAgent.call(dialUri, options);
    });
};

export const endCall = () => (dispatch, getState) => {
    dispatch(addMeetingLog('MeetingCallClient#endCall will end call'));
    return new Promise((resolve, reject) => {
        const state = getState();
        const meetingUserAgent = selectActiveMeetingCallUserAgent(state);

        if (meetingUserAgent) {
            dispatch(
                addMeetingLog(
                    'MeetingCallClient#endCall stopping the user agent',
                ),
            );
            // Shut down the UserAgent
            meetingUserAgent.stop();
        } else {
            // Just stop the call, don't mess with the softphone UserAgent
            dispatch(
                addMeetingLog(
                    'MeetingCallClient#endCall stopping call session since we are using the softphone user agent',
                ),
            );
            const callSession = selectActiveMeetingCallSession(state);
            if (callSession) {
                try {
                    callSession.terminate();
                } catch (e) {
                    dispatch(
                        addMeetingLog(
                            `MeetingCallClient#endCall Could not terminate call, ${
                                e && e.message
                            }`,
                        ),
                    );
                    return reject(e);
                }
            }
        }
        return resolve();
    });
};
